import axios from "axios";
import moment from "moment-timezone";
import { useState } from "react";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import ActionsMenu from "../../../components/ActionsMenu";
import DeleteConfirmationModal from "../../../components/ConfirmationModals/DeleteConfirmationModal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { formatPhoneNumber } from "../../../utils/utils";

const PropertyDetailHeader = ({ propertydata }) => {
  const { id } = useParams();
  const [config] = useAuth();
  const navigate = useNavigate();

  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = () => {
    setDisableDelete(true);

    axios
      .get(`${BASE_URL}/delete-property/${id}`, config)
      .then((res) => {
        if (res.data.success) {
          navigate("/properties");
        }
        setShowDeleteModal(false);

        if (res?.data?.message) {
          NotificationManager.success(res.data.message);
        }
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response.data.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-3">

      {/* Header */}
      <div className="flex items-start justify-between border-b pb-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {propertydata?.property_name}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {propertydata?.property_type?.type}
          </p>
        </div>

        <ActionsMenu
          handleEdit={() => navigate(`/edit-property/${id}`)}
          handleDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {/* Top Grid */}
     <div className="grid grid-cols-5 gap-6 mt-3 items-start">

        {/* Last Updated */}
        <div>
          <p className="text-xs text-gray-500">Last Update</p>
          <p className="body-N  font-medium text-gray-900">
            {moment(propertydata?.updated_at)
              .tz("America/New_York")
              .format("MM/DD/YYYY")}
          </p>
        </div>

        {/* Store */}
        <div>
          <p className="text-xs text-gray-500">Store #</p>
          <p className="text-sm font-medium text-gray-900">
            {propertydata?.store || "-"}
          </p>
        </div>

        {/* Address */}
        <div>
          <p className="text-xs text-gray-500">Address</p>
          <p className="body-N  font-medium text-gray-900">
            {propertydata?.street_address
              ? `${propertydata.street_address}, ${propertydata.city}, ${propertydata.state}, ${propertydata.zipcode}`
              : "-"}
          </p>
        </div>

        {/* Links */}
        <div>
          <p className="text-xs text-gray-500">Links</p>

          <div className="flex flex-col gap-2 mt-1">
            {propertydata?.google_map_link && (
              <button
                onClick={() =>
                  window.open(propertydata.google_map_link, "_blank")
                }
                className="text-green-600 text-sm underline hover:text-green-800 text-left"
              >
                View on Google Maps
              </button>
            )}

            {propertydata?.rpr_link && (
              <button
                onClick={() => window.open(propertydata.rpr_link, "_blank")}
                className="border px-2 py-1 rounded-md text-xs w-fit hover:bg-gray-100"
              >
                RPR Link
              </button>
            )}
          </div>
        </div>

        {/* Owner */}
        <div>
          <p className="text-xs text-gray-500">Owner</p>

          <div className="">
            <span className="body-N text-green-600 truncate">
              {propertydata?.owner?.name}
            </span>

            {propertydata?.owner_phone?.[0]?.phone_number && (
              <span className=" body-N text-gray-600 truncate">
                {formatPhoneNumber(propertydata.owner_phone[0].phone_number)}
              </span>
            )}

            {propertydata?.owner_phone?.[0]?.phone_category && (
              <span className="body-N  text-green-600 capitalize">
                {propertydata.owner_phone[0].phone_category.replace(/_/g, " ")}
              </span>
            )}
          </div>
        </div>

      </div>

      {/* Bottom Section */}
      <div className="grid md:grid-cols-3 gap-6 mt-8 border-t pt-6">

        {/* Comments */}
        <div>
          <p className="text-xs text-gray-500">Comments</p>
          <p className="text-sm text-gray-800 mt-1">
            {propertydata?.comment || "No comments"}
          </p>
        </div>

        {/* Property Type */}
        <div>
          <p className="text-xs text-gray-500">Property Type</p>
          <span className="inline-block mt-2 px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full">
            {propertydata?.property_type?.type}
          </span>
        </div>

        {/* Tags */}
        <div>
          <p className="text-xs text-gray-500">Tags</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {propertydata?.propertytag_details?.map((el, i) => (
              <span
                key={i}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
              >
                {el?.tag_name}
              </span>
            ))}
          </div>
        </div>

      </div>

      {/* Delete Modal */}
      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default PropertyDetailHeader;